import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronsUpDownIcon } from 'lucide-react';
import { clusters, phenotypeCue, phenotypeHeadline, phenotypeSupport } from '../../data/teaser';
import { ClusterImage } from '../ClusterImage';
const EASE = [0.23, 1, 0.32, 1] as const;
interface PhenotypesScreenProps {
  /** Storyboard only — renders the screen with a specific cluster selected. */
  previewIndex?: number;
}
export function PhenotypesScreen({
  previewIndex
}: PhenotypesScreenProps = {}) {
  const [index, setIndex] = useState(previewIndex ?? 0);
  const active = clusters[index];
  const move = (delta: number) => setIndex(i => Math.min(clusters.length - 1, Math.max(0, i + delta)));
  return <div className="flex flex-1 flex-col" data-id="element-614" data-mp="1h20u8ybvzmmq">
      <motion.h2 initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease: EASE
    }} className="font-display text-[26px] font-bold uppercase leading-[0.98] tracking-tight text-navy" data-id="element-615" data-mp="36goyude8k0a7">
        {phenotypeHeadline[0]}
        <span className="mt-1 block text-blue" data-id="element-616" data-mp="27ovhtum5tz6x">{phenotypeHeadline[1]}</span>
      </motion.h2>

      <p className="mt-2.5 text-[12px] leading-relaxed text-mut" data-id="element-617" data-mp="2lg8zf30icmzb">{phenotypeSupport}</p>

      <div className="mt-4 flex flex-1 gap-3" data-id="element-618" data-mp="2li3alplz90q7">
        {/* Vertical cluster slider — makes the four phenotypes explicitly steppable */}
        <div className="flex shrink-0 flex-col items-center gap-1.5" role="tablist" aria-orientation="vertical" aria-label="DME phenotype clusters" data-id="element-619" data-mp="1tokswx4euuoe">
          {clusters.map((c, i) => {
          const isActive = i === index;
          return <button key={c.id} type="button" role="tab" aria-selected={isActive} onClick={() => setIndex(i)} className={`flex w-8 flex-1 flex-col items-center justify-center gap-1 rounded-lg border text-[10px] font-bold tabular-nums transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${isActive ? 'border-blue bg-blue text-paper' : 'border-line bg-mist text-mut hover:border-blue'}`} data-id="element-620" data-mp="1xqeo4pvu76h6" data-mpi={i}>
                <span className="text-[8px] font-medium uppercase tracking-micro opacity-70" data-id="element-621" data-mp="2kqwriqv22gbf">
                  CL
                </span>
                {i + 1}
              </button>;
        })}
          <ChevronsUpDownIcon className="mt-0.5 h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-622" data-mp="1s428zzhstr0l" />
        </div>

        <motion.div drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={0.12} onDragEnd={(_, info) => {
        if (info.offset.y < -40) move(1);
        if (info.offset.y > 40) move(-1);
      }} className="min-w-0 flex-1 cursor-grab active:cursor-grabbing" data-id="element-623" data-mp="1x12i6j9y39vx">
          <AnimatePresence mode="wait" data-id="element-624" data-mp="11tie64mc67r1">
            <motion.article key={active.id} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.24,
            ease: EASE
          }} className={`overflow-hidden rounded-xl border bg-paper ${active.advanced ? 'border-band/40' : 'border-line'}`} data-id="element-625" data-mp="11s77nx1ialw8">
              <ClusterImage cluster={active} className="aspect-[16/10]" data-id="element-626" data-mp="ivz0gnoxo0ya" />
              <div className="px-3 py-3" data-id="element-627" data-mp="e2wccv2xdjph">
                <p className="font-display text-[15px] font-bold uppercase tracking-tight text-navy" data-id="element-628" data-mp="e24ippsw1w1u">
                  {active.name}
                </p>
                <p className="mt-0.5 text-[12px] leading-tight text-mut" data-id="element-629" data-mp="46t55at7uuvo">{active.descriptor}</p>
                <p className="mt-2.5 border-t border-line pt-2.5 text-[11px] leading-snug text-navy2" data-id="element-630" data-mp="34uukd79l6q">
                  {active.signature}
                </p>
              </div>
            </motion.article>
          </AnimatePresence>

          <p className="mt-2 text-center text-[9px] uppercase tracking-micro text-mut" data-id="element-631" data-mp="1v9fmp25eh1xp">
            Swipe or tap CL 1–4 · {index + 1} of {clusters.length}
          </p>
        </motion.div>
      </div>

      <p className="mt-3 text-[9px] uppercase tracking-micro text-mut" data-id="element-632" data-mp="1lffakgbpawfh">{phenotypeCue}</p>
    </div>;
}